import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Users, Plus, Edit, Trash2, User, Heart, Calendar, MapPin, Briefcase } from 'lucide-react'

export default function FamilyTree() {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const { isAuthenticated, isAdmin, getAuthToken } = useAuth()

  useEffect(() => {
    loadFamilyMembers()
  }, [])

  const loadFamilyMembers = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/family`)
      const data = await response.json()
      if (data.success) {
        setMembers(data.data.members)
      }
    } catch (error) {
      console.error('Error loading family members:', error)
    } finally {
      setLoading(false)
    }
  }

  // Build family tree structure
  const buildFamilyTree = () => {
    // Find root members (those without parents)
    const roots = members.filter(m => !m.fatherId && !m.motherId)
    
    // Group by generation
    const generations = []
    const processed = new Set()
    
    const addGeneration = (parentIds) => {
      const children = members.filter(m => 
        (parentIds.includes(m.fatherId) || parentIds.includes(m.motherId)) &&
        !processed.has(m.id)
      )
      
      if (children.length > 0) {
        children.forEach(c => processed.add(c.id))
        generations.push(children)
        addGeneration(children.map(c => c.id))
      }
    }
    
    if (roots.length > 0) {
      roots.forEach(r => processed.add(r.id))
      generations.push(roots)
      addGeneration(roots.map(r => r.id))
    }
    
    return generations
  }

  const generations = buildFamilyTree()

  const getSpouseName = (spouseId) => {
    const spouse = members.find(m => m.id === spouseId)
    return spouse ? `${spouse.firstName} ${spouse.lastName}` : 'Unknown'
  }

  if (loading) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="content-container p-12">
            <div className="flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="content-container p-8 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center">
                <Users className="w-8 h-8 mr-3 text-primary-600" />
                Kapcheboin Family Tree
              </h1>
              <p className="text-xl text-gray-700">
                Preserving our genealogical heritage across generations
              </p>
            </div>
            {isAuthenticated && (
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Member</span>
              </button>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card-glass p-6">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Members</p>
                <p className="text-2xl font-bold text-gray-900">{members.length}</p>
              </div>
            </div>
          </div>
          <div className="card-glass p-6">
            <div className="flex items-center">
              <Heart className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Living Members</p>
                <p className="text-2xl font-bold text-gray-900">
                  {members.filter(m => m.isAlive).length}
                </p>
              </div>
            </div>
          </div>
          <div className="card-glass p-6">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Generations</p>
                <p className="text-2xl font-bold text-gray-900">{generations.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Family Tree Visualization */}
        <div className="content-container p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Family Tree</h2>
          
          {generations.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No family members added yet</p>
              {isAuthenticated && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="btn-primary"
                >
                  Add First Member
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-12">
              {generations.map((generation, genIndex) => (
                <div key={genIndex} className="relative">
                  <div className="text-sm font-semibold text-gray-500 mb-4">
                    Generation {genIndex + 1}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {generation.map((member) => (
                      <div key={member.id} className="card-glass p-6 hover:shadow-xl transition-all">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              member.gender === 'male' ? 'bg-blue-100' : 'bg-pink-100'
                            }`}>
                              <User className={`w-6 h-6 ${
                                member.gender === 'male' ? 'text-blue-600' : 'text-pink-600'
                              }`} />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {member.firstName} {member.lastName}
                              </h3>
                              {member.maidenName && (
                                <p className="text-xs text-gray-500">née {member.maidenName}</p>
                              )}
                            </div>
                          </div>
                          {!member.isAlive && (
                            <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                              Deceased
                            </span>
                          )}
                        </div>

                        <div className="space-y-2 text-sm text-gray-600">
                          {member.birthDate && (
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4" />
                              <span>Born: {new Date(member.birthDate).toLocaleDateString()}</span>
                            </div>
                          )}
                          {member.birthPlace && (
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-4 h-4" />
                              <span>{member.birthPlace}</span>
                            </div>
                          )}
                          {member.occupation && (
                            <div className="flex items-center space-x-2">
                              <Briefcase className="w-4 h-4" />
                              <span>{member.occupation}</span>
                            </div>
                          )}
                          {member.spouseIds && member.spouseIds.length > 0 && (
                            <div className="flex items-center space-x-2">
                              <Heart className="w-4 h-4" />
                              <span>Spouse: {getSpouseName(member.spouseIds[0])}</span>
                            </div>
                          )}
                        </div>

                        {member.biography && (
                          <p className="mt-3 text-sm text-gray-700 line-clamp-2">
                            {member.biography}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* All Members List */}
        <div className="content-container p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">All Family Members</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Birth Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added By</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {members.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          member.gender === 'male' ? 'bg-blue-100' : 'bg-pink-100'
                        }`}>
                          <User className={`w-4 h-4 ${
                            member.gender === 'male' ? 'text-blue-600' : 'text-pink-600'
                          }`} />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {member.firstName} {member.lastName}
                          </div>
                          {member.maidenName && (
                            <div className="text-xs text-gray-500">née {member.maidenName}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.birthDate ? new Date(member.birthDate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        member.isAlive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {member.isAlive ? 'Living' : 'Deceased'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.addedBy}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
